import React, { useEffect, useState } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

const stripePromise = loadStripe(
  "dfds"
);

/* ===================== STRIPE FORM ===================== */

const StripePaymentForm = ({ clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;

    setLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success.html`,
      },
    });

    if (error) {
      alert(error.message);
    }

    setLoading(false);
  };

  return (
    <>
      <PaymentElement />
      <button
        onClick={handlePay}
        disabled={!stripe || loading}
        className="w-full bg-black text-white py-3 rounded-lg mt-4 font-semibold"
      >
        {loading ? "Processing..." : "Pay Securely"}
      </button>
    </>
  );
};

/* ===================== MAIN PAGE ===================== */

export default function SelfPayment() {
  const [gateway, setGateway] = useState("stripe");
  const [clientSecret, setClientSecret] = useState(null);

  // Normally fetched via tokenized payment link
  const booking = {
    id: "BK-23901",
    amount: 459.0,
    description: "Flight Booking – New York to London",
  };

  /* ---------- Create Stripe Payment Intent ---------- */
  useEffect(() => {
    if (gateway !== "stripe") return;

    axios
      .post("/api/create-stripe-intent", {
        amount: booking.amount,
        booking_id: booking.id,
      })
      .then((res) => setClientSecret(res.data.clientSecret))
      .catch(() => alert("Failed to initialize payment"));
  }, [gateway]);

  /* ---------- Load NMI Collect.js ---------- */
  useEffect(() => {
    if (gateway !== "nmi") return;

    const script = document.createElement("script");
    script.src = "https://secure.nmi.com/token/Collect.js";
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      window.CollectJS.configure({
        paymentSelector: "#pay-nmi",
        variant: "inline",
        fields: {
          ccnumber: { selector: "#ccnumber" },
          ccexp: { selector: "#ccexp" },
          cvv: { selector: "#cvv" },
        },
        callback: (response) => {
          axios
            .post("/api/charge-nmi", {
              token: response.token,
              booking_id: booking.id,
              amount: booking.amount,
            })
            .then(() => (window.location.href = "/payment-success.html"))
            .catch(() => alert("Payment failed"));
        },
      });
    };
  }, [gateway]);

  
  
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white rounded-xl shadow-xl p-6">
        {/* Header */}
        <h2 className="text-2xl font-bold text-center mb-2">
          Secure Payment
        </h2>
        <p className="text-center text-gray-500 mb-6">
          Complete your payment safely
        </p>

        {/* Booking Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
          <p><b>Booking ID:</b> {booking.id}</p>
          <p><b>Description:</b> {booking.description}</p>
          <p className="text-lg font-semibold mt-2">
            Amount: ${booking.amount.toFixed(2)}
          </p>
        </div>

        {/* Gateway Switch */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setGateway("stripe")}
            className={`flex-1 py-2 rounded-lg border ${
              gateway === "stripe"
                ? "border-black bg-black text-white"
                : "border-gray-300"
            }`}
          >
            Card / Apple Pay / ACH
          </button>

          <button
            onClick={() => setGateway("nmi")}
            className={`flex-1 py-2 rounded-lg border ${
              gateway === "nmi"
                ? "border-black bg-black text-white"
                : "border-gray-300"
            }`}
          >
            Card Only
          </button>
        </div>

        {/* Payment Area */}
        {gateway === "stripe" && clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <StripePaymentForm clientSecret={clientSecret} />
          </Elements>
        )}

        {gateway === "nmi" && (
          <>
            <div className="space-y-3">
              <input id="ccnumber" placeholder="Card Number" className="input" />
              <input id="ccexp" placeholder="MM / YY" className="input" />
              <input id="cvv" placeholder="CVV" className="input" />
            </div>

            <button
              id="pay-nmi"
              className="w-full bg-black text-white py-3 rounded-lg mt-4 font-semibold"
            >
              Pay Securely 
            </button>
          </>
        )}

        {/* Footer */}
        <p className="text-xs text-gray-400 text-center mt-6">
          🔒 256-bit encryption • PCI-DSS compliant • No card data stored
        </p>
      </div>
    </div>
  );
}


//usd , gbp, aud, cad, eur, nzd, aed,